import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award, BookOpen, CheckCircle2, ExternalLink, GraduationCap, Loader2, Users,
} from 'lucide-react';
import { annoncerErreur, EtatErreur, EtatSansDonnees, SqueletteListe } from '../etats';
import DomainesParticipant from './DomainesParticipant';

/* ═══════════════════════════════════════════════════════════════════════
   LES MODULES DE FORMATION D'UN PROJET — catalogue par domaine, avancement
   de chacun, attestations

   Fichier IDENTIQUE dans Front-end/admin (le coach, onglet « Formation »
   d'un projet) et Front-end/user (le dirigeant, page « Modules de
   formation ») : seuls les appels au serveur diffèrent, passés en `api`.

   Un domaine compte plusieurs modules : on filtre par domaine, on attribue
   par domaine, l'attestation se délivre par domaine terminé.
   ═══════════════════════════════════════════════════════════════════════ */

const STATUT = {
  non_commence: { libelle: 'À faire', classe: 'text-[var(--text-muted)] bg-[var(--bg-main)]' },
  en_cours: { libelle: 'En cours', classe: 'text-[#a16207] bg-[#fefce8]' },
  termine: { libelle: 'Terminé', classe: 'text-[var(--accent-texte)] bg-[var(--vert-pale)]' },
};

function Barre({ valeur }) {
  const v = Math.max(0, Math.min(100, Number(valeur) || 0));
  return (
    <div className="h-2 rounded-full bg-[var(--bg-main)] overflow-hidden" aria-hidden="true">
      <div className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-500" style={{ width: `${v}%` }} />
    </div>
  );
}

function BoutonAttestation({ telecharger, libelle }) {
  const [envoi, setEnvoi] = useState(false);
  const cliquer = async () => {
    setEnvoi(true);
    try {
      await telecharger();
    } catch (e) {
      annoncerErreur(e.message || 'L’attestation n’a pas pu être téléchargée.');
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <button type="button" onClick={cliquer} disabled={envoi} aria-busy={envoi || undefined}
            className="act act-sm act-contour" title={`Attestation de suivi : ${libelle}`}>
      {envoi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
      {libelle}
    </button>
  );
}

function Personne({ p, filtre, onAttribuer, telechargerAttestation }) {
  const modules = filtre ? p.modules.filter((m) => m.domaine_id === filtre) : p.modules;
  const termines = modules.filter((m) => m.statut === 'termine').length;
  const progression = modules.length ? Math.round(modules.reduce((s, m) => s + (Number(m.taux) || 0), 0) / modules.length) : 0;
  const domaines = [...new Set(p.modules.map((m) => m.domaine).filter(Boolean))];
  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-56">
          <p className="font-semibold text-[var(--text-bold)]">
            {p.nom}
            <span className="ml-2 text-[11px] font-semibold text-[var(--text-muted)]">
              {p.responsable ? 'Dirigeant · suit la formation retenue' : (p.role === 'DIRIGEANT' ? 'Dirigeant' : 'Membre')}
            </span>
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {domaines.length ? domaines.join(' · ') : 'Aucun domaine attribué'}
          </p>
        </div>
        <div className="w-full sm:w-56">
          <div className="flex justify-between text-[11.5px] text-[var(--text-muted)] mb-1 tabular-nums">
            <span>{termines} / {modules.length} module{modules.length > 1 ? 's' : ''} terminé{termines > 1 ? 's' : ''}</span>
            <span className="font-semibold text-[var(--text-bold)]">{progression} %</span>
          </div>
          <Barre valeur={progression} />
        </div>
        {onAttribuer && p.participant_id && (
          <button type="button" onClick={() => onAttribuer(p)} className="act act-sm act-contour">
            <GraduationCap className="w-3.5 h-3.5" /> Domaines
          </button>
        )}
      </div>

      {modules.length > 0 && (
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {modules.map((m) => {
            const s = STATUT[m.statut] || STATUT.non_commence;
            return (
              <li key={m.id} className="flex items-center gap-2 text-[12.5px] min-w-0">
                {m.statut === 'termine'
                  ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-[var(--accent-texte)]" />
                  : <BookOpen className="w-3.5 h-3.5 shrink-0 text-[var(--text-muted)]" />}
                <span className="truncate text-[var(--text-main)]">{m.titre}</span>
                <span className={`ml-auto shrink-0 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${s.classe}`}>
                  {m.statut === 'en_cours' ? `${Math.round(m.taux)} %` : s.libelle}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {p.attestations.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11.5px] font-semibold text-[var(--text-muted)]">Attestations :</span>
          {p.attestations.filter((a) => !filtre || a.domaine_id === filtre).map((a) => (
            <BoutonAttestation key={a.domaine_id} libelle={a.domaine}
                               telecharger={() => telechargerAttestation(a.domaine_id, p.participant_id)} />
          ))}
        </div>
      )}
    </li>
  );
}

/* `api` : { equipe(projetId), domaines: { perimetre, lire, ecrire } (pour la
   fenêtre d'attribution), attestation(projetId, domaineId, participantId) }. */
export default function FormationEquipe({ projetId, api }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [cible, setCible] = useState(null);

  // La page peut recréer `api` à chaque rendu : le garder en référence évite
  // de recharger en boucle.
  const apiRef = useRef(api);
  apiRef.current = api;

  const charger = useCallback(() => {
    setErreur(null);
    apiRef.current.equipe(projetId).then(setDonnees).catch(setErreur);
  }, [projetId]);
  useEffect(() => { charger(); }, [charger]);

  const domaines = useMemo(() => donnees?.domaines || [], [donnees]);
  const visibles = filtre ? domaines.filter((d) => d.id === filtre) : domaines;
  const apiDomaines = useMemo(() => ({ ...apiRef.current.domaines, perimetre: async () => domaines }), [domaines]);

  if (erreur && !donnees) return <div className="bg-[var(--bg-card)] rounded-carte shadow-carte"><EtatErreur erreur={erreur} onReessayer={charger} /></div>;
  if (!donnees) return <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6"><SqueletteListe lignes={5} /></div>;

  return (
    <div className="space-y-6">
      {domaines.length > 1 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par domaine">
          {[{ id: '', nom: 'Tous les domaines', n: domaines.reduce((s, d) => s + d.modules.length, 0) }]
            .concat(domaines.map((d) => ({ id: d.id, nom: d.nom, n: d.modules.length })))
            .map((d) => (
              <button key={d.id || 'tous'} type="button" onClick={() => setFiltre(d.id)} aria-pressed={filtre === d.id}
                      className={`px-3.5 py-1.5 rounded-controle text-[12.5px] font-semibold border transition-colors ${
                        filtre === d.id
                          ? 'bg-[var(--accent)] text-[#0A1128] border-transparent'
                          : 'bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)] hover:text-[var(--text-bold)]'}`}>
                {d.nom} <span className="opacity-60">{d.n}</span>
              </button>
            ))}
        </div>
      )}

      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <h2 className="titre-carte flex items-center gap-2 px-5 pt-5 pb-3">
          <Users className="w-5 h-5 text-[var(--accent-texte)]" /> Avancement de l’équipe
        </h2>
        {donnees.personnes.length === 0 ? (
          <div className="px-5 pb-5">
            <EtatSansDonnees cas="vide" icone={Users} titre="Personne ne suit encore de module"
                             aide="Ajoutez des personnes dans « Participants », puis attribuez-leur des domaines." />
          </div>
        ) : (
          <ul className="divide-y divide-[var(--border)] border-t border-[var(--border)]">
            {donnees.personnes.map((p) => (
              <Personne key={p.participant_id || `u-${p.user_id}`} p={p} filtre={filtre}
                        onAttribuer={donnees.peut_attribuer ? setCible : null}
                        telechargerAttestation={(domaineId, participantId) => apiRef.current.attestation(projetId, domaineId, participantId)} />
            ))}
          </ul>
        )}
      </section>

      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
        <h2 className="titre-carte flex items-center gap-2 mb-1">
          <BookOpen className="w-5 h-5 text-[var(--accent-texte)]" /> Modules de l’Académie
        </h2>
        <p className="text-xs text-[var(--text-muted)] mb-4">
          {donnees.nature === 'formation'
            ? 'Les modules retenus pour cette formation, par domaine.'
            : 'Tous les modules sont compris dans l’accompagnement, par domaine.'}
        </p>
        {visibles.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            {donnees.nature === 'formation'
              ? 'Aucun module retenu : choisissez-les dans « Espace formation ».'
              : 'Aucun module n’est encore publié sur l’Académie.'}
          </p>
        ) : (
          <div className="space-y-5">
            {visibles.map((d) => (
              <div key={d.id || d.nom}>
                <h3 className="text-sm font-semibold text-[var(--text-bold)] mb-2">{d.nom}</h3>
                <ul className="grid gap-2 md:grid-cols-2">
                  {d.modules.map((m) => (
                    <li key={m.id} className="border border-[var(--border)] rounded-pastille px-4 py-3 flex items-start gap-3">
                      <BookOpen className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent-texte)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[var(--text-bold)]">{m.titre}</span>
                        {m.description && <span className="block text-xs text-[var(--text-muted)] line-clamp-2">{m.description}</span>}
                      </span>
                      {m.lien ? (
                        <a href={m.lien} target="_blank" rel="noopener noreferrer" className="act act-sm act-tertiaire shrink-0"
                           aria-label={`Ouvrir « ${m.titre} » sur l’Académie`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <span className="shrink-0 text-[11px] text-[var(--text-muted)]">Bientôt disponible</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {cible && (
        <DomainesParticipant
          participant={{ id: cible.participant_id, nom_affichage: cible.nom }}
          api={apiDomaines}
          onClose={() => setCible(null)}
          onSaved={charger}
        />
      )}
    </div>
  );
}

